import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Check, Download, X } from "lucide-react";

import { EmptyState } from "@/components/feedback/empty-state";
import { Cabecera } from "@/components/propuesta/cabecera";
import { Tile } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/lote/$loteId")({
  component: Lote,
});

// ===========================================================================
// La revisión de completitud de un lote (paso 4 del guion, issue #44).
//
// «Primero se hace la revisión de la completitud de la información, si tiene
// todas las secciones, y te sale un cuadro de cuáles cumplieron con los
// requisitos y cuáles no, y puedes descargar un Excel con el resumen. Luego
// puedes ver en una tabla todos los documentos escaneados y guardados por filas
// y sus datos extraídos necesarios, y lo puedes dejar en segundo plano.»
//
// Los tres párrafos de ese pedido son tres cosas y esta pantalla es las tres:
// el cuadro de arriba, la tabla de abajo con lo extraído, y el hecho de que se
// pueda cerrar y volver — el trabajo sigue en la cola.
//
// LOS INCOMPLETOS VAN PRIMERO. Es una pantalla para pedir lo que falta: si los
// veinte incompletos quedan repartidos entre 273 filas correctas, no sirve.
// ===========================================================================

type Expediente = {
  archivoId: string;
  nombre: string;
  estado: string;
  serieNumero: string | null;
  cliente: string | null;
  total: number | null;
  moneda: "PEN" | "USD" | null;
  paginas: number | null;
  tieneComprobante: boolean;
  tieneFicha: boolean;
  vouchers: number;
  completo: boolean;
  falta: string | null;
};

const dinero = (c: number | null, m: "PEN" | "USD" | null) =>
  c === null
    ? "—"
    : `${m === "USD" ? "US$" : "S/"} ${(c / 100).toLocaleString("es-PE", { minimumFractionDigits: 2 })}`;

function Lote() {
  const { loteId } = Route.useParams();
  const { data, isPending } = api.lote.useQuery(
    ["lote", loteId],
    { params: { loteId } },
    {
      // Mientras queden archivos en la cola la pantalla se refresca sola: el
      // pipeline tarda minutos con 273 expedientes, y pedirle a alguien que
      // recargue para ver si ya terminó es pedirle que vigile una barra.
      refetchInterval: (q: {
        state: { data?: { status: number; body?: { enCurso?: number } } };
      }) => {
        const b = q.state.data;
        return b?.status === 200 && (b.body?.enCurso ?? 0) > 0 ? 3000 : false;
      },
    },
  );
  const d = data?.status === 200 ? data.body : null;
  const n = (v: number) => v.toLocaleString("es-PE");

  if (isPending) {
    return (
      <>
        <Cabecera migas={["Documentos"]} titulo="Revisión del lote" />
        <Skeleton className="h-96 w-full" />
      </>
    );
  }

  if (!d) {
    return (
      <>
        <Cabecera migas={["Documentos"]} titulo="Revisión del lote" />
        <EmptyState
          title="Ese lote no está"
          message="Puede que se haya descartado, o que el enlace sea de un periodo que ya se vació."
          action={
            <Button variant="outline" asChild>
              <Link to="/carga">
                <ArrowLeft className="mr-1.5 size-4" />
                Volver a cargar
              </Link>
            </Button>
          }
        />
      </>
    );
  }

  // Los que hay que mirar, arriba. Dentro de cada grupo, por nombre.
  const ordenados = [...(d.expedientes as Expediente[])].sort((a, b) => {
    if (a.completo !== b.completo) return a.completo ? 1 : -1;
    return a.nombre.localeCompare(b.nombre, "es");
  });

  return (
    <>
      <Cabecera
        migas={["Documentos", "Revisión del lote"]}
        titulo={`${n(d.archivos)} archivos subidos`}
        acciones={
          <>
            <Button size="sm" variant="outline" asChild>
              <a href={`/carga/lote/${loteId}/resumen.xlsx`} download>
                <Download className="mr-2 size-4" />
                Descargar el resumen
              </a>
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/carga">Cargar más</Link>
            </Button>
          </>
        }
      />

      {d.enCurso > 0 ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-cyan-500/40 bg-cyan-500/5 px-3 py-2.5 text-[12.5px]">
          <span className="size-2 animate-pulse rounded-full bg-cyan-500" />
          Quedan <b>{n(d.enCurso)}</b> por leer. Esta pantalla se actualiza
          sola, y podés cerrarla: el trabajo sigue en la cola.
        </div>
      ) : null}

      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <Tile titulo="Archivos del lote" valor={n(d.archivos)} tono="neutro" />
        <Tile
          titulo="Completos"
          subtitulo="boleta y al menos un voucher"
          valor={n(d.completos)}
          tono="ok"
        />
        <Tile
          titulo="Incompletos"
          subtitulo="hay que pedir lo que falta"
          valor={n(d.incompletos)}
          tono={d.incompletos > 0 ? "bad" : "neutro"}
        />
        <Tile
          titulo="No se pudieron leer"
          valor={n(d.fallados)}
          tono={d.fallados > 0 ? "bad" : "neutro"}
        />
      </div>

      {/* Contenedor propio con scroll: la tabla puede traer 273 filas y sin
          esto crece por debajo del recorte de la página, que es el fallo de
          #37 en otra pantalla. */}
      <div className="min-h-0 overflow-auto rounded-lg border">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 bg-background">
            <tr className="border-b text-[10.5px] text-muted-foreground uppercase tracking-wider">
              <th className="px-3 py-2 text-left font-semibold">Expediente</th>
              <th className="px-2 py-2 text-left font-semibold">Boleta</th>
              <th className="px-2 py-2 text-left font-semibold">Cliente</th>
              <th className="px-2 py-2 text-right font-semibold">Total</th>
              <th className="px-2 py-2 text-center font-semibold">Págs.</th>
              <th className="px-2 py-2 text-center font-semibold">Ficha</th>
              <th className="px-2 py-2 text-center font-semibold">Vouchers</th>
              <th className="px-3 py-2 text-left font-semibold">Estado</th>
            </tr>
          </thead>
          <tbody>
            {ordenados.map((e) => (
              <tr
                key={e.archivoId}
                className={cn(
                  "border-b last:border-0",
                  !e.completo && e.estado === "leido" && "bg-red-600/5",
                )}
              >
                <td className="max-w-[320px] px-3 py-1.5">
                  <Link
                    to="/archivo/$id"
                    params={{ id: e.archivoId }}
                    className="block truncate hover:underline"
                    title={e.nombre}
                  >
                    {e.nombre}
                  </Link>
                </td>
                <td className="px-2 py-1.5 tabular-nums">
                  {e.serieNumero ?? "—"}
                </td>
                <td className="max-w-[220px] truncate px-2 py-1.5">
                  {e.cliente ?? "—"}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {dinero(e.total, e.moneda)}
                </td>
                <td className="px-2 py-1.5 text-center tabular-nums">
                  {e.paginas ?? "—"}
                </td>
                <td className="px-2 py-1.5 text-center">
                  {e.tieneFicha ? (
                    <Check className="mx-auto size-3.5 text-emerald-600" />
                  ) : (
                    <X className="mx-auto size-3.5 text-muted-foreground/50" />
                  )}
                </td>
                <td className="px-2 py-1.5 text-center tabular-nums">
                  {e.vouchers > 0 ? (
                    e.vouchers
                  ) : (
                    <X className="mx-auto size-3.5 text-red-600" />
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {e.completo ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-500">
                      <Check className="size-3.5" />
                      completo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-500">
                      <AlertTriangle className="size-3.5 shrink-0" />
                      {e.falta}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11.5px] text-muted-foreground">
        Un expediente está completo si trae su boleta o factura y al menos un
        voucher. La ficha del cliente se cuenta y se avisa, pero no bloquea: de
        los 273 cargados en agosto, 55 no la traían y se aceptaron igual. Está
        pendiente de que contabilidad confirme la regla.
      </p>
    </>
  );
}
